import React from 'react';
import { Box, HStack, Select, Text, VStack } from '@chakra-ui/react';
import { Icon } from 'src/components/atoms/Icon';
import { AreaGraph } from 'src/components/molecules/graphs/AreaGraph';
import { LegendContextProvider } from 'src/components/molecules/Legend/LegendContext/LegendContextProvider';
import { DataSet } from 'src/features/DataUpload/types';
import {
  calculateTotal,
  formatChartData,
  formatTotal,
  formatYAxis,
  getTimePeriod,
} from 'src/features/DataUpload/utils/dataVisualizationHelpers';

type DataSetPanelProps = {
  dataSet: DataSet;
  isExpanded: boolean;
  isLastItem: boolean;
  onToggle: (id: string) => void;
  availableMetrics: Record<string, string[]>;
  availableMediaSources: Record<string, string[]>;
  selectedMetrics: Record<string, string>;
  selectedMediaSources: Record<string, string>;
  onMetricChange: (
    dataType: string,
  ) => (event: React.ChangeEvent<HTMLSelectElement>) => void;
  onMediaSourceChange: (
    dataType: string,
  ) => (event: React.ChangeEvent<HTMLSelectElement>) => void;
};

export const DataSetPanel: React.FC<DataSetPanelProps> = ({
  dataSet,
  isExpanded,
  isLastItem,
  onToggle,
  availableMetrics,
  availableMediaSources,
  selectedMetrics,
  selectedMediaSources,
  onMetricChange,
  onMediaSourceChange,
}) => {
  const dataTypeKey = dataSet.id.replace('-data', '');
  const currentMetric = selectedMetrics[dataTypeKey] || '';
  const formattedMetricLabel = currentMetric
    ? currentMetric.charAt(0).toUpperCase() + currentMetric.slice(1)
    : '';
  const chartData = formatChartData(dataSet.data);
  const mediaSources = availableMediaSources[dataTypeKey] || [];
  const metricOptions = availableMetrics[dataTypeKey] || [];
  const timePeriod = getTimePeriod(chartData);

  return (
    <Box>
      <Box
        p={4}
        bg={isExpanded ? 'background.lightGrey' : 'transparent'}
        borderTopRadius="12px"
        border={isExpanded ? '1px solid' : 'none'}
        borderColor={isExpanded ? 'gray.200' : 'transparent'}
        cursor="pointer"
        onClick={() => onToggle(dataSet.id)}
        _hover={{ bg: 'surface.hover' }}
        transition="all 0.2s">
        <HStack spacing={3} align="center">
          <Icon
            iconName={isExpanded ? 'chevron-down' : 'chevron-right'}
            w="16px"
            h="16px"
            color="text.light.secondary"
          />
          <Text fontSize="md" fontWeight={500} color="text.light.primary">
            {dataSet.name}
          </Text>
        </HStack>
      </Box>

      {isExpanded && (
        <Box px={3} py={4} bg="surface.control">
          <Box
            mt={2}
            p={6}
            bg="surface.card"
            borderRadius="8px"
            border="1px solid"
            borderColor="border.layout">
            <VStack spacing={4} align="stretch" mb={6}>
              <HStack justify="space-between" align="center">
                <Text fontSize="lg" fontWeight={600} color="gray.800">
                  Data
                </Text>
                <HStack spacing={3}>
                  {dataSet.id !== 'kpi-data' && mediaSources.length > 0 && (
                    <Select
                      w="216px"
                      h="44px"
                      variant="filled"
                      borderRadius="8px"
                      fontSize="sm"
                      bg="surface.control"
                      value={selectedMediaSources[dataTypeKey]}
                      onChange={onMediaSourceChange(dataTypeKey)}>
                      <option value="all">All Media/Sources</option>
                      {mediaSources.map((mediaSource) => (
                        <option key={mediaSource} value={mediaSource}>
                          {mediaSource}
                        </option>
                      ))}
                    </Select>
                  )}
                  <Select
                    w="216px"
                    h="44px"
                    variant="filled"
                    borderRadius="8px"
                    fontSize="sm"
                    bg="surface.control"
                    value={currentMetric}
                    onChange={onMetricChange(dataTypeKey)}>
                    {metricOptions.map((metric) => (
                      <option key={metric} value={metric}>
                        {metric.charAt(0).toUpperCase() + metric.slice(1)}
                      </option>
                    ))}
                  </Select>
                </HStack>
              </HStack>
            </VStack>

            <Box h="400px" w="100%">
              <LegendContextProvider
                keyColorMap={{ value: 'green' }}
                lineKeys={['value']}>
                <AreaGraph
                  data={chartData}
                  xAxisKey="date"
                  xAxisFormatter={(value) => value.toString()}
                  yAxisFormatter={(value) =>
                    formatYAxis(value, dataSet.id, selectedMetrics)
                  }
                />
              </LegendContextProvider>
            </Box>

            <Box mt={4} p={4} bg="surface.control" borderRadius="8px">
              <VStack spacing={3} align="stretch">
                <HStack justify="space-between" align="center">
                  <Text fontSize="md" fontWeight={500} color="gray.700">
                    {`Total ${formattedMetricLabel}:`}
                  </Text>
                  <Text fontSize="xl" fontWeight={700} color="gray.900">
                    {formatTotal(
                      calculateTotal(chartData),
                      dataSet.id,
                      selectedMetrics,
                    )}
                  </Text>
                </HStack>
                {timePeriod && (
                  <HStack justify="space-between" align="center">
                    <Text fontSize="sm" fontWeight={400} color="gray.600">
                      Time Period:
                    </Text>
                    <Text fontSize="sm" fontWeight={500} color="gray.700">
                      {timePeriod.startDate} - {timePeriod.endDate}
                    </Text>
                  </HStack>
                )}
              </VStack>
            </Box>
          </Box>
        </Box>
      )}

      {!isLastItem && <Box h="1px" bg="gray.200" mt={3} />}
    </Box>
  );
};
